import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { CKEditor } from '~/components/Shared/CKEditor4/CKEditor4';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { GradientOutline } from '~/eds/src/components/GradientOutline/GradientOutline';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Variation } from '~/features/guidelines/variation/Variation';
import { GuidelineClauseVariation } from '~/redux/api/types/playbooks/guidelines';

import { editorConfig } from '../../constants';

type GuidelineValue = {
  name: string;
  guideline: string;
  sample: string;
  suggest_redlines: boolean;
};

type Props = {
  value: GuidelineValue;
  variations: Array<GuidelineClauseVariation>;

  onChange: (value: GuidelineValue) => void;
  onAddNewVariation: () => void;
  onAddExistingVariation: () => void;
  onDelete: () => void;
  onDeleteVariation?: (id: string) => void;
};

export const Guideline = ({
  value,
  variations,

  onChange,
  onAddExistingVariation,
  onDelete,
  onDeleteVariation,
}: Props) => {
  const intl = useIntl();
  const [collapsed, toggleCollapsed] = useToggle(false);

  const handleOnChangeEditor = useCallback(
    (field: 'guideline' | 'sample') => (editor: any) => {
      onChange({ ...value, [field]: editor.getData() as string });
    },
    [value, onChange],
  );

  return (
    <GradientOutline>
      <Layout direction="column" spacing={1} flex="1">
        <Layout
          onClick={toggleCollapsed}
          py={3}
          px={4}
          align="center"
          justify="space-between"
        >
          <Text>{value.name}</Text>
          <Icon icon={collapsed ? 'chevron-right' : 'chevron-down'} />
        </Layout>

        {!collapsed && (
          <Layout px={3} spacing={3} direction="column" flex="1">
            <FormField
              input={TextInput}
              label={intl.formatMessage({
                id: 'Guideline.nameDescription',
                defaultMessage: 'Guideline Name',
                description: 'Description for guideline name input field',
              })}
              value={value.name}
              onChange={(v) => {
                onChange({ ...value, name: v as unknown as string });
              }}
              inputProps={{
                maxLength: 100,
              }}
              name="name"
              placeholder={intl.formatMessage({
                id: 'Guideline.namePlaceholder',
                defaultMessage: 'e.g. Exact Data',
                description: 'Placeholder for guideline name input field',
              })}
            />

            <FormField
              input={CKEditor}
              inputProps={{
                config: editorConfig,
                data: value.guideline,
              }}
              label={intl.formatMessage({
                id: 'Guideline.guidelineDescription',
                defaultMessage: 'Guideline',
                description: 'Description for guideline input field',
              })}
              value={value.guideline}
              onChange={handleOnChangeEditor('guideline')}
              name="guideline"
              placeholder={intl.formatMessage({
                id: 'Guideline.guidelinePlaceholder',
                defaultMessage:
                  'e.g. Define Exact Data: List file names, database tables, or specific document titles.',
                description: 'Placeholder for guideline input field',
              })}
            />
            <FormField
              input={CKEditor}
              inputProps={{
                config: editorConfig,
                data: value.sample,
              }}
              label={intl.formatMessage({
                id: 'Guideline.sampleWordingDescription',
                defaultMessage: 'Sample Wording (optional)',
                description: 'Description for sample wording textarea',
              })}
              value={value.sample}
              onChange={handleOnChangeEditor('sample')}
              name="sampleWording"
              placeholder={intl.formatMessage({
                id: 'Guideline.sampleWordingPlaceholder',
                defaultMessage:
                  'e.g. "Exact Data encompasses categories of sensitive information, including financial records, customer data, and intellectual property, or any data designated as confidential and requiring heightened security."',
                description: 'Placeholder for sample wording textarea',
              })}
            />

            <Layout direction="column" spacing={2}>
              {variations.map((variation) => (
                <Variation
                  key={variation.clause_variation_id}
                  variation={variation}
                  onDelete={() =>
                    onDeleteVariation?.(variation.clause_variation_id)
                  }
                />
              ))}
            </Layout>

            <Layout align="center" justify="space-between">
              <Checkbox
                option={{
                  label: intl.formatMessage({
                    id: 'Guideline.suggestRedlinesLabel',
                    defaultMessage: 'Suggest Redlines (optional)',
                    description: 'Label for suggest redlines checkbox',
                  }),
                  value: true,
                }}
                value={value.suggest_redlines ?? true}
                onChange={(v) => {
                  onChange({ ...value, suggest_redlines: v as boolean });
                }}
                name="suggestRedlines"
              />
              <IconButton icon="trash" onClick={onDelete} />
            </Layout>

            <Layout flex="1" mb={3}>
              <Box flex="1">
                <Button
                  isFullWidth
                  variant="secondary"
                  onClick={onAddExistingVariation}
                  icon="plus"
                  iconPosition="left"
                  text={intl.formatMessage({
                    id: 'Guideline.addExistingVariation',
                    defaultMessage: 'Add Existing Clause Variation',
                    description: 'Button label to add an existing variation',
                  })}
                />
              </Box>
              {/* TODO: Re enable once product decide the direction of new variations */}
              {/* <Box flex="1" ml={2}>
                <Button
                  isFullWidth
                  variant="secondary"
                  onClick={onAddNewVariation}
                  text={intl.formatMessage({
                    id: 'Guideline.addNewVariation',
                    defaultMessage: 'Add New Clause Variation',
                    description: 'Button label to add a new variation',
                  })}
                  icon="plus"
                  iconPosition="left"
                />
              </Box> */}
            </Layout>
          </Layout>
        )}
      </Layout>
    </GradientOutline>
  );
};
